import api from './api';

// Types matching backend DTOs
export interface NotificationHistoryItem {
  id: number;
  notificationType: string; // e.g., "BookingConfirmation", "BookingReminder", "PaymentReceived"
  channel: string; // "Email", "SMS", "Push"
  status: string; // "Pending", "Sent", "Failed", "Delivered", "Opened", "Clicked"
  subject: string | null;
  content: string | null;
  recipientAddress: string | null;
  sentAt: string;
  deliveredAt: string | null;
  openedAt: string | null;
  createdAt: string;
  isRead?: boolean;
}

export interface NotificationHistoryResponse {
  items: NotificationHistoryItem[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

export interface UnreadCountResponse {
  count: number;
}

// Notification type categories for filtering
export const NotificationTypeCategories = {
  Bookings: ['BookingConfirmation', 'BookingReminder', 'BookingUpdate', 'BookingCancellation'],
  Payments: ['PaymentReceived', 'PaymentFailed', 'RefundProcessed', 'SubscriptionRenewal'],
  Security: ['PasswordReset', 'SecurityAlert', 'LoginAlert', 'AccountUpdate'],
  Marketing: ['Promotion', 'Newsletter', 'Announcement'],
} as const;

// Channel types
export type NotificationChannel = 'Email' | 'SMS' | 'Push' | 'All';

// Notification status types
export type NotificationStatus = 'Pending' | 'Sent' | 'Failed' | 'Delivered' | 'Opened' | 'Clicked';

// Helper function to get notification type category
export const getNotificationCategory = (notificationType: string): string => {
  for (const [category, types] of Object.entries(NotificationTypeCategories)) {
    if ((types as readonly string[]).includes(notificationType)) {
      return category;
    }
  }
  return 'Other';
};

// Helper function to get user-friendly notification type label
export const getNotificationTypeLabel = (notificationType: string): string => {
  const labels: Record<string, string> = {
    BookingConfirmation: 'Booking Confirmed',
    BookingReminder: 'Appointment Reminder',
    BookingUpdate: 'Booking Updated',
    BookingCancellation: 'Booking Cancelled',
    PaymentReceived: 'Payment Received',
    PaymentFailed: 'Payment Failed',
    RefundProcessed: 'Refund Processed',
    SubscriptionRenewal: 'Subscription Renewed',
    PasswordReset: 'Password Reset',
    SecurityAlert: 'Security Alert',
    LoginAlert: 'New Login',
    AccountUpdate: 'Account Updated',
    Promotion: 'Special Offer',
    Newsletter: 'Newsletter',
    Announcement: 'Announcement',
  };
  return labels[notificationType] || notificationType;
};

// Helper function to get status color class
export const getStatusColorClass = (status: string): { bg: string; text: string } => {
  const statusColors: Record<string, { bg: string; text: string }> = {
    Pending: { bg: 'bg-yellow-100 dark:bg-yellow-900', text: 'text-yellow-800 dark:text-yellow-200' },
    Sent: { bg: 'bg-blue-100 dark:bg-blue-900', text: 'text-blue-800 dark:text-blue-200' },
    Failed: { bg: 'bg-red-100 dark:bg-red-900', text: 'text-red-800 dark:text-red-200' },
    Delivered: { bg: 'bg-green-100 dark:bg-green-900', text: 'text-green-800 dark:text-green-200' },
    Opened: { bg: 'bg-purple-100 dark:bg-purple-900', text: 'text-purple-800 dark:text-purple-200' },
    Clicked: { bg: 'bg-indigo-100 dark:bg-indigo-900', text: 'text-indigo-800 dark:text-indigo-200' },
  };
  return statusColors[status] || { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-800 dark:text-gray-200' };
};

// API functions
export const notificationHistoryApi = {
  /**
   * Get notification history for the current user (paginated)
   */
  getNotificationHistory: async (
    pageNumber: number = 1,
    pageSize: number = 20
  ): Promise<NotificationHistoryResponse> => {
    const response = await api.get('/mobile/notifications/history', {
      params: { pageNumber, pageSize },
    });
    return response.data;
  },

  /**
   * Mark a notification as read
   */
  markAsRead: async (notificationId: number): Promise<void> => {
    await api.put(`/mobile/notifications/${notificationId}/read`);
  },

  /**
   * Get unread notification count
   */
  getUnreadCount: async (): Promise<number> => {
    const response = await api.get('/mobile/notifications/unread/count');
    return response.data.count ?? response.data;
  },

};

export default notificationHistoryApi;
